import React, {useEffect, useLayoutEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Flex,
    SimpleGrid,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import {renderLayoutsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import ItemsCounter from './items-counter'
import ProductGridItemBoundary from './product-grid-item-boundary'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {mergeRefs} from '../../../utils/ref-utils'
import {
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {
    fillGDIResponsiveInheritance,
    hasGDIViewTypes
} from '../../../components/optimizely/gdi/utils/gdi-styles'
import {parseResponsiveViewTypes} from '../../../components/optimizely/gdi/utils/gdi-responsive'

const ProductGrid = ({
    // Products
    allProducts,
    productSearchResult,
    skeletonPlaceholders,
    // Loading states
    isRefetching,
    isFetched,
    isCategoryChanging,
    searchParams,
    // Layout
    isAlternativeGridView,
    gridColumns,
    // CMS
    currentBreakpoint,
    gridInterruptorPositions,
    // Enrichment / tracking
    enrichProduct,
    observeProduct,
    productRefs,
    prependScrollAnchor,
    // Wishlist
    wishlist,
    // Handlers
    addItemToWishlist,
    removeItemFromWishlist,
    handleProductClick,
    // Pagination
    total,
    maxPageLoaded,
    goToPage,
    // Auto-load
    autoLoadDone,
    onAutoLoad,
    isSearch,
    // Variable per-page limits
    getLimitForPage,
    loadedLimitTotal
}) => {
    const {formatMessage} = useIntl()
    const sentinelRef = useRef(null)
    const pgStyles = useMultiStyleConfig('ProductGrid')

    // After each DOM commit, if a prepend scroll anchor is active, restore the
    // anchored product tile to its original viewport position. This fires for
    // both the skeleton-insert render and the real-product-prepend render.
    useLayoutEffect(() => {
        const anchor = prependScrollAnchor?.current
        if (!anchor) return
        const el = productRefs.current[anchor.productId]
        if (!el) return
        const currentTop = el.getBoundingClientRect().top
        const delta = currentTop - anchor.viewportTop
        if (Math.abs(delta) > 1) {
            window.scrollBy(0, delta)
        }
    })

    // Clear the scroll anchor once the prepended products have fully loaded
    // (skeletons are gone and allProducts[0] has changed).
    useEffect(() => {
        const anchor = prependScrollAnchor?.current
        if (!anchor) return
        const hasPrependSkeletons = skeletonPlaceholders?.some((s) => s.position === 'prepend')
        if (!hasPrependSkeletons && allProducts?.[0]?.productId !== anchor.productId) {
            // Prepend is complete — do one final adjustment then clear.
            const el = productRefs.current[anchor.productId]
            if (el) {
                const currentTop = el.getBoundingClientRect().top
                const delta = currentTop - anchor.viewportTop
                if (Math.abs(delta) > 1) {
                    window.scrollBy(0, delta)
                }
            }
            prependScrollAnchor.current = null
        }
    }, [allProducts, skeletonPlaceholders, prependScrollAnchor, productRefs])

    useEffect(() => {
        if (isSearch) return
        const sentinel = sentinelRef.current
        if (!sentinel || autoLoadDone) return

        // Don't set up the observer when all products already fit on one page
        const currentProducts = allProducts.length > 0 ? allProducts : productSearchResult?.hits
        const loadedCount = currentProducts?.length ?? 0
        if (loadedCount >= (total ?? 0)) return

        // Don't set up the observer if loaded pages already cover all products.
        // This prevents unnecessary fetches during back-navigation state restoration
        // where allProducts may not be fully accumulated yet but maxPageLoaded is correct.
        if (total != null && (loadedLimitTotal ?? 0) >= total) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && window.scrollY > 0) {
                    observer.disconnect()
                    onAutoLoad()
                }
            },
            {threshold: 0.1}
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [autoLoadDone, onAutoLoad, isSearch, allProducts, productSearchResult, total])

    const products =
        allProducts.length > 0 ? allProducts : productSearchResult?.hits

    // gridInterruptorPositions is `{ position: GDIBlock[] }` already filtered to
    // the active category & current breakpoint by the parent hook.
    const interruptorPositions = gridInterruptorPositions || {}

    /* Per-breakpoint column count of the product grid (mirrors
     * `gridColumns = [2, 2, 4, 4]` in use-product-list.js → base/sm/md/lg).
     * Used to cap an interruptor's authored ColSpan so a 2-wide tile inside
     * a 2-column mobile grid still fits, and so slot accounting knows how
     * many product slots the interruptor displaces. */
    const BREAKPOINT_TO_GRID_COLS = {
        mobile: gridColumns?.[0] ?? 2,
        tablet: gridColumns?.[2] ?? gridColumns?.[1] ?? 4,
        desktop: gridColumns?.[3] ?? gridColumns?.[2] ?? 4,
        xl: gridColumns?.[3] ?? 4
    }
    const maxGridCols = BREAKPOINT_TO_GRID_COLS[currentBreakpoint] ?? 4

    /* Read the authored ColSpan on a GDIContentTileBlock-based interruptor
     * for the current breakpoint. Returns 1 (1x1) by default; honours 2x1
     * when authored. Capped at the parent grid's column count. */
    const getInterruptorColSpan = (block) => {
        if (block?.__typename !== 'GDIContentTileBlock') return 1
        const stylesArr = block.GDIContentTileBlockStylesPerViewType
        if (!Array.isArray(stylesArr)) return 1
        const targetBreakpoint =
            currentBreakpoint === 'desktop' || currentBreakpoint === 'xl'
                ? 'lg'
                : currentBreakpoint === 'tablet'
                ? 'md'
                : 'base'
        const spanByBreakpoint = {}

        const setSpan = (entry, breakpoints) => {
            const n = Number(entry?.ColSpan)
            if (!Number.isFinite(n) || n <= 0) return
            breakpoints.forEach((bp) => {
                spanByBreakpoint[bp] = n
            })
        }

        stylesArr.filter((entry) => !hasGDIViewTypes(entry)).forEach((entry) => {
            setSpan(entry, ['base', 'md', 'lg'])
        })

        stylesArr.filter((entry) => hasGDIViewTypes(entry)).forEach((entry) => {
            setSpan(entry, parseResponsiveViewTypes(entry.ViewTypesToApply))
        })

        const inheritedSpan = fillGDIResponsiveInheritance(spanByBreakpoint)

        return Math.min(inheritedSpan[targetBreakpoint] ?? 1, maxGridCols)
    }

    // Each GDI interruptor child occupies as many grid cells as its authored
    // ColSpan (defaulting to 1). The block of children at a given position
    // contributes the sum of their spans.
    const getInterruptorSlotCount = (position) => {
        const blocks = interruptorPositions[position]
        if (!Array.isArray(blocks)) return 0
        return blocks.reduce((acc, b) => acc + getInterruptorColSpan(b), 0)
    }

    // Cap rendering so each loaded page shows exactly its page-size worth of grid
    // items (products + interruptors). Once all products are fetched, remove the cap.
    const allFetched = (products?.length ?? 0) >= total
    const maxGridSlots = allFetched ? Infinity : (loadedLimitTotal ?? maxPageLoaded * searchParams.limit)
    let visibleProductCount = 0
    let gridSlotCount = 0
    if (products) {
        for (let i = 0; i < products.length; i++) {
            if (gridSlotCount >= maxGridSlots) break
            const interruptorSlots = getInterruptorSlotCount(i + 1)
            const slotsNeeded = interruptorSlots + 1
            if (gridSlotCount + slotsNeeded > maxGridSlots) break
            gridSlotCount += slotsNeeded
            visibleProductCount++
        }
    }
    const visibleProducts = products?.slice(0, visibleProductCount) || []

    // hasMore is true only when there are more products *after* the currently
    // loaded pages (i.e. the next-page offset is within the total count). Using
    // loadedLimitTotal (= getOffsetForPage(maxPageLoaded + 1)) rather than
    // visibleProductCount prevents "Show More" from appearing after back-
    // navigation when the restored page is the last one and no further pages exist.
    const hasMore = total != null && (loadedLimitTotal ?? 0) < total

    return (
        <Box>
            <SimpleGrid
                columns={gridColumns}
                spacingX={2}
                spacingY={isAlternativeGridView ? 0 : 6}
            >
                {isHydrated() &&
                ((isRefetching && !allProducts?.length) ||
                    !productSearchResult ||
                    isCategoryChanging) ? (
                    new Array(searchParams.limit)
                        .fill(0)
                        .map((value, index) => <ProductTileSkeleton key={index} />)
                ) : (
                    <>
                        {skeletonPlaceholders
                            ?.filter((s) => s.position === 'prepend')
                            .map((s) => (
                                <ProductTileSkeleton key={s.id} />
                            ))}

                        {(() => {
                            const itemsToRender = []
                            visibleProducts?.forEach((productSearchItem, index) => {
                                const position = index + 1
                                const productId = productSearchItem.productId
                                const isInWishlist =
                                    !!wishlist?.customerProductListItems?.find(
                                        (item) => item.productId === productId
                                    )

                                if (interruptorPositions[position] && !isAlternativeGridView) {
                                    interruptorPositions[position].forEach((block, blockIndex) => {
                                        const interruptorSpan = getInterruptorColSpan(block)
                                        itemsToRender.push(
                                            <ProductGridItemBoundary
                                                key={`grid-interruptor-${position}`}
                                                resetKey={`interruptor-${position}-${currentBreakpoint}`}
                                                errorContext={{
                                                    itemType: 'gridInterruptor',
                                                    position,
                                                    breakpoint: currentBreakpoint
                                                }}
                                            >
                                                <Box
                                                    key={`grid-interruptor-${position}-${blockIndex}`}
                                                    data-testid={`grid-interruptor-${position}`}
                                                    /* Span the parent SimpleGrid by the
                                                     * authored ColSpan so a 2x1 inter-
                                                     * ruptor occupies two product slots
                                                     * horizontally. This Box is the
                                                     * direct grid child (the boundary
                                                     * just passes children through), so
                                                     * the span has to live here. */
                                                    gridColumn={
                                                        interruptorSpan > 1
                                                            ? `span ${interruptorSpan}`
                                                            : undefined
                                                    }
                                                    /* Pixel-perfect: match the product
                                                     * tile IMAGE height (image Chakra
                                                     * AspectRatio is locked to 19/28 via
                                                     * theme `mediaAspect.ratio`).
                                                     *
                                                     * Target height = colWidth * 28/19.
                                                     * Wrapper width W = N*col + (N-1)*8.
                                                     * Therefore:
                                                     *   height = W*(28/(19N)) - (N-1)*8*(28/(19N))
                                                     *
                                                     * CSS can't express that via
                                                     * `aspect-ratio`, but the legacy
                                                     * padding-top trick can — percentage
                                                     * padding is resolved against parent
                                                     * width, so on a `width: 100%` child
                                                     * it equals percent * wrapper_width.
                                                     * Inner content is absolutely
                                                     * positioned to fill the spacer.
                                                     * `alignSelf:start` stops CSS Grid
                                                     * from stretching the wrapper to the
                                                     * (taller) row height. */
                                                    alignSelf="start"
                                                    position="relative"
                                                    width="100%"
                                                    paddingTop={
                                                        interruptorSpan === 1
                                                            ? '147.3684%' /* 28/19 */
                                                            : `calc(${
                                                                  (28 / (19 * interruptorSpan)) *
                                                                  100
                                                              }% - ${
                                                                  (((interruptorSpan - 1) * 8 * 28) /
                                                                      (19 * interruptorSpan)
                                                                  ).toFixed(4)
                                                              }px)`
                                                    }
                                                >
                                                    <Box position="absolute" inset={0}>
                                                        {renderLayoutsSwitch(block, {
                                                            position,
                                                            breakpoint: currentBreakpoint,
                                                            /* Signal to
                                                             * GDIContentTileBlock that it
                                                             * should size to the
                                                             * SimpleGrid row (= product
                                                             * tile image height) rather
                                                             * than imposing its authored
                                                             * aspect ratio. */
                                                            isGridInterruptor: true
                                                        })}
                                                    </Box>
                                                </Box>
                                            </ProductGridItemBoundary>
                                        )
                                    })
                                }

                                itemsToRender.push(
                                    <ProductGridItemBoundary
                                        key={productSearchItem.productId}
                                        resetKey={`${productSearchItem.productId}-${isAlternativeGridView ? 'alternative' : 'default'}`}
                                        fallback={<ProductTileSkeleton />}
                                        errorContext={{
                                            itemType: 'productTile',
                                            productId,
                                            gridView: isAlternativeGridView ? 'alternative' : 'default'
                                        }}
                                    >
                                        <ProductTile
                                            showScrollHint
                                            data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                            data-product-id={productSearchItem.productId}
                                            ref={mergeRefs((el) => {
                                                if (el) productRefs.current[productId] = el
                                            }, observeProduct)}
                                            product={enrichProduct(productSearchItem)}
                                            enableFavourite={!isAlternativeGridView}
                                            isFavourite={isInWishlist}
                                            isRefreshingData={isRefetching && isFetched}
                                            imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                                            selectableAttributeId={PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID}
                                            showSwatches={!isAlternativeGridView}
                                            showVideo={!isAlternativeGridView}
                                            showQV={!isAlternativeGridView}
                                            showDetails={!isAlternativeGridView}
                                            onClick={() =>
                                                handleProductClick({productSearchItem, productId, index})
                                            }
                                            onFavouriteToggle={(toBeFavourite) => {
                                                const action = toBeFavourite
                                                    ? addItemToWishlist
                                                    : removeItemFromWishlist
                                                return action(productSearchItem, index)
                                            }}
                                            dynamicImageProps={{
                                                widths: ['50vw', '50vw', '25vw', '25vw', '25vw'],
                                                imageProps: {
                                                    loading: index < 4 ? 'eager' : 'lazy',
                                                    fetchpriority: index === 0 ? 'high' : 'auto',
                                                    decoding: index < 4 ? 'sync' : 'async'
                                                }
                                            }}
                                            videoProps={{
                                                preload: index < 4 ? 'auto' : 'none'
                                            }}
                                        />
                                    </ProductGridItemBoundary>
                                )
                            })
                            return itemsToRender
                        })()}

                        {skeletonPlaceholders
                            ?.filter((s) => s.position === 'append')
                            .map((s) => (
                                <ProductTileSkeleton key={s.id} />
                            ))}
                    </>
                )}
            </SimpleGrid>

            {/* Sentinel for IntersectionObserver-based auto-load */}
            <div ref={sentinelRef} aria-hidden="true" style={{height: '1px'}} />

            {(autoLoadDone || isSearch) && hasMore && (
                <Flex flexDirection="column" alignItems="center" gap={8} {...pgStyles.showMoreContainer}>
                    <ItemsCounter
                        loadedCount={visibleProductCount}
                        totalCount={total}
                    />
                    <Button
                        variant="webPrimary"
                        onClick={() => goToPage(maxPageLoaded + 1)}
                        {...pgStyles.showMoreButton}
                    >
                        {formatMessage({id: 'plp.button.show.more', defaultMessage: 'Show More'})}
                    </Button>
                </Flex>
            )}
        </Box>
    )
}

ProductGrid.propTypes = {
    allProducts: PropTypes.array.isRequired,
    productSearchResult: PropTypes.object,
    skeletonPlaceholders: PropTypes.array.isRequired,
    isRefetching: PropTypes.bool,
    isFetched: PropTypes.bool,
    isCategoryChanging: PropTypes.bool,
    searchParams: PropTypes.object.isRequired,
    isAlternativeGridView: PropTypes.bool,
    gridColumns: PropTypes.array.isRequired,
    currentBreakpoint: PropTypes.string,
    gridInterruptorPositions: PropTypes.object,
    enrichProduct: PropTypes.func.isRequired,
    observeProduct: PropTypes.object,
    productRefs: PropTypes.object.isRequired,
    prependScrollAnchor: PropTypes.object,
    wishlist: PropTypes.object,
    addItemToWishlist: PropTypes.func.isRequired,
    removeItemFromWishlist: PropTypes.func.isRequired,
    handleProductClick: PropTypes.func.isRequired,
    total: PropTypes.number,
    page: PropTypes.number,
    minPageLoaded: PropTypes.number,
    maxPageLoaded: PropTypes.number,
    goToPage: PropTypes.func.isRequired,
    autoLoadDone: PropTypes.bool,
    onAutoLoad: PropTypes.func.isRequired,
    isSearch: PropTypes.bool,
    getLimitForPage: PropTypes.func,
    loadedLimitTotal: PropTypes.number
}

export default ProductGrid
